import { useEffect, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid2';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { ApiError } from '../api/client';
import { mikrotikApi, servicePlansApi, type ServicePlanFormValues } from '../api/endpoints';
import { PageHeader } from '../components/PageHeader';
import { BulkImportDialog } from '../components/BulkImportDialog';
import { StatusBadge } from '../components/StatusBadge';
import { EmptyState } from '../components/EmptyState';
import { TableSkeleton } from '../components/TableSkeleton';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { useToast } from '../app/ToastProvider';
import { formatPeso } from '../utils/format';
import type { ServicePlan } from '../api/types';

const EMPTY_FORM: ServicePlanFormValues & { description: string } = {
  planCode: '',
  planName: '',
  downloadSpeedMbps: 0,
  uploadSpeedMbps: 0,
  monthlyFee: 0,
  description: '',
  mikrotikRemoteAddress: '',
  mikrotikAddressList: '',
};

interface PlanDialogProps {
  open: boolean;
  /** When set, the dialog retires this plan and creates its replacement. */
  replacing?: ServicePlan;
  onClose: () => void;
}

function PlanDialog({ open, replacing, onClose }: PlanDialogProps) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  const toast = useToast();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!open) return;
    setForm(
      replacing
        ? {
            planCode: replacing.planCode,
            planName: replacing.planName,
            downloadSpeedMbps: replacing.downloadSpeedMbps,
            uploadSpeedMbps: replacing.uploadSpeedMbps,
            monthlyFee: replacing.monthlyFee,
            description: replacing.description ?? '',
            mikrotikRemoteAddress: replacing.mikrotikRemoteAddress ?? '',
            mikrotikAddressList: replacing.mikrotikAddressList ?? '',
          }
        : EMPTY_FORM,
    );
    setFieldErrors({});
    setError(null);
  }, [open, replacing]);

  const { data: routerOptions, isError: routerOptionsError } = useQuery({
    queryKey: ['mikrotik', 'service-plan-options'],
    queryFn: () => mikrotikApi.servicePlanOptions(),
    enabled: open,
    retry: false,
  });

  const remoteAddresses = [...new Set([
    ...(routerOptions?.remoteAddresses ?? []),
    ...(form.mikrotikRemoteAddress ? [form.mikrotikRemoteAddress] : []),
  ])];
  const addressLists = [...new Set([
    ...(routerOptions?.addressLists ?? []),
    ...(form.mikrotikAddressList ? [form.mikrotikAddressList] : []),
  ])];
  const mutation = useMutation({
    mutationFn: () => {
      const payload: ServicePlanFormValues = {
        planCode: form.planCode.trim(),
        planName: form.planName.trim(),
        downloadSpeedMbps: Number(form.downloadSpeedMbps),
        uploadSpeedMbps: Number(form.uploadSpeedMbps),
        monthlyFee: Number(form.monthlyFee),
        description: form.description.trim() || undefined,
        mikrotikRemoteAddress: form.mikrotikRemoteAddress?.trim() || undefined,
        mikrotikAddressList: form.mikrotikAddressList?.trim() || undefined,
      };
      return replacing
        ? servicePlansApi.update(replacing.id, payload)
        : servicePlansApi.create(payload);
    },
    onSuccess: async ({ message }) => {
      toast.success(message);
      await queryClient.invalidateQueries({ queryKey: ['service-plans'] });
      onClose();
    },
    onError: (caught: unknown) => {
      if (caught instanceof ApiError) {
        setFieldErrors(caught.byField());
        setError(caught.message);
      } else {
        setError('The service plan could not be saved.');
      }
    },
  });

  const set =
    (key: keyof typeof EMPTY_FORM) =>
    (event: { target: { value: string } }): void => {
      setForm((current) => ({ ...current, [key]: event.target.value }));
    };

  const handleSubmit = (event: FormEvent): void => {
    event.preventDefault();
    setError(null);
    setFieldErrors({});
    mutation.mutate();
  };

  return (
    <Dialog open={open} onClose={mutation.isPending ? undefined : onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit} noValidate>
        <DialogTitle>{replacing ? `Edit ${replacing.planName}` : 'Add Service Plan'}</DialogTitle>
        <DialogContent dividers>
          {error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          ) : null}


          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 5 }}>
              <TextField
                label="Plan Code"
                value={form.planCode}
                onChange={set('planCode')}
                error={Boolean(fieldErrors.planCode)}
                helperText={fieldErrors.planCode ?? 'Unique, e.g. BFC-110'}
                required
                disabled={Boolean(replacing)}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 7 }}>
              <TextField
                label="Plan Name"
                value={form.planName}
                onChange={set('planName')}
                error={Boolean(fieldErrors.planName)}
                helperText={fieldErrors.planName}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                select
                label="Remote Address"
                value={form.mikrotikRemoteAddress ?? ''}
                onChange={set('mikrotikRemoteAddress')}
                helperText="RouterOS: IP → Pools"
              >
                <MenuItem value="">None</MenuItem>
                {remoteAddresses.map((name) => <MenuItem key={name} value={name}>{name}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                select
                label="Address List"
                value={form.mikrotikAddressList ?? ''}
                onChange={set('mikrotikAddressList')}
                helperText="RouterOS: IP → Firewall → Address Lists"
              >
                <MenuItem value="">None</MenuItem>
                {addressLists.map((name) => <MenuItem key={name} value={name}>{name}</MenuItem>)}
              </TextField>
            </Grid>
            {routerOptionsError ? (
              <Grid size={12}>
                <Alert severity="warning">RouterOS options could not be loaded. Check the router connection and reopen this dialog.</Alert>
              </Grid>
            ) : null}            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                label="Download (Mbps)"
                type="number"
                value={form.downloadSpeedMbps}
                onChange={set('downloadSpeedMbps')}
                error={Boolean(fieldErrors.downloadSpeedMbps)}
                helperText={fieldErrors.downloadSpeedMbps}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                label="Upload (Mbps)"
                type="number"
                value={form.uploadSpeedMbps}
                onChange={set('uploadSpeedMbps')}
                error={Boolean(fieldErrors.uploadSpeedMbps)}
                helperText={fieldErrors.uploadSpeedMbps}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextField
                label="Monthly Fee"
                type="number"
                value={form.monthlyFee}
                onChange={set('monthlyFee')}
                error={Boolean(fieldErrors.monthlyFee)}
                helperText={fieldErrors.monthlyFee ?? 'PHP per month'}
                slotProps={{ htmlInput: { min: 0, step: 0.01 } }}
                required
              />
            </Grid>
            <Grid size={12}>
              <TextField
                label="Description"
                value={form.description}
                onChange={set('description')}
                multiline
                minRows={2}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={onClose} color="inherit" disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={mutation.isPending}>
            {mutation.isPending ? 'Saving…' : replacing ? 'Save Changes' : 'Save Plan'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

const COLUMN_COUNT = 8;

export function ServicePlansPage() {
  const [includeRetired, setIncludeRetired] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [replacing, setReplacing] = useState<ServicePlan | null>(null);
  const [deleting, setDeleting] = useState<ServicePlan | null>(null);
  const queryClient = useQueryClient();
  const toast = useToast();
  const deleteMutation = useMutation({
    mutationFn: (id: number) => servicePlansApi.remove(id),
    onSuccess: async ({ message }) => {
      toast.success(message);
      setDeleting(null);
      await queryClient.invalidateQueries({ queryKey: ['service-plans'] });
    },
    onError: (caught: unknown) => {
      toast.error(caught instanceof Error ? caught.message : 'The service plan could not be deleted.');
    },
  });

  const { data, isPending, isError, error } = useQuery({
    queryKey: ['service-plans', includeRetired],
    queryFn: () => servicePlansApi.list(includeRetired),
  });

  return (
    <>
      <PageHeader
        title="Service Plans"
        description="Internet packages offered to subscribers, grouped by upstream ISP."
        actions={
          <>
            <Button variant="outlined" startIcon={<UploadFileIcon />} onClick={() => setImportOpen(true)}>
              Bulk Import
            </Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAddOpen(true)}>Add Plan</Button>
          </>
        }
      />

      {isError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error instanceof Error ? error.message : 'Service plans could not be loaded.'}
        </Alert>
      ) : null}

      <Card>
        <Stack direction="row" sx={{ p: 2 }}>
          <FormControlLabel
            control={
              <Switch
                checked={includeRetired}
                onChange={(event) => setIncludeRetired(event.target.checked)}
              />
            }
            label="Show retired plans"
          />
        </Stack>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Plan Code</TableCell>
                <TableCell>Plan Name</TableCell>
                <TableCell>ISP</TableCell>
                <TableCell align="right">Download</TableCell>
                <TableCell align="right">Upload</TableCell>
                <TableCell align="right">Monthly Fee</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isPending ? (
                <TableSkeleton columns={COLUMN_COUNT} />
              ) : data && data.length > 0 ? (
                data.map((plan) => (
                  <TableRow key={plan.id} hover>
                    <TableCell>{plan.planCode}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {plan.planName}
                      </Typography>
                      {plan.description ? (
                        <Typography variant="caption" color="text.secondary">
                          {plan.description}
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell>{plan.mikrotikRemoteAddress ?? '-'}</TableCell>
                    <TableCell align="right">{plan.downloadSpeedMbps} Mbps</TableCell>
                    <TableCell align="right">{plan.uploadSpeedMbps} Mbps</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {formatPeso(plan.monthlyFee)}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <StatusBadge status={plan.isActive ? 'ACTIVE' : 'INACTIVE'} />
                        <Typography variant="caption" color="text.secondary">
                          {plan._count?.subscriptions ?? 0} subscriber(s)
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <Button size="small" startIcon={<EditOutlinedIcon />} onClick={() => setReplacing(plan)}>
                          Edit
                        </Button>
                        <Button
                          size="small"
                          color="error"
                          startIcon={<DeleteOutlineIcon />}
                          onClick={() => setDeleting(plan)}
                        >
                          Delete
                        </Button>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={COLUMN_COUNT} sx={{ border: 0 }}>
                    <EmptyState message="No service plans found." />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      <BulkImportDialog open={importOpen} initialStep={0} onClose={() => setImportOpen(false)} />
      <PlanDialog open={addOpen} onClose={() => setAddOpen(false)} />
      <ConfirmDialog
        open={deleting !== null}
        title="Delete service plan?"
        description={deleting ? `Delete ${deleting.planCode} — ${deleting.planName}? Plans with subscribers cannot be deleted.` : ""}
        confirmLabel="Delete Plan"
        danger
        busy={deleteMutation.isPending}
        onCancel={() => setDeleting(null)}
        onConfirm={() => deleting && deleteMutation.mutate(deleting.id)}
      />
      <PlanDialog
        open={replacing !== null}
        replacing={replacing ?? undefined}
        onClose={() => setReplacing(null)}
      />
    </>
  );
}
